import { List } from '@kerfjs/ui/list';
import { ListItem } from '@kerfjs/ui/list-item';
import { ValueTable, ValueTableRow } from '@kerfjs/ui/value-table';
import { type SafeHtml } from 'kerfjs';

import { memberDialog, orgs, settings } from '../../data/state.js';
import { isOrgAdmin } from '../../lib/context.js';
import { MemberDialog } from './member-dialog.js';
import { MemberInviteDialog } from './member-invite-dialog.js';
import { memberDisplayName, type MemberRow } from './member-row.js';

export { invitationEmailStatus, memberDisplayName, type MemberRow } from './member-row.js';

/**
 * Members and pending invitations. Admins get one row per member that opens the
 * member dialog (HS2-20JG34) and invite from the header's "New Member…" dialog
 * (HS2-FJ9J2K); other staff see a read-only table.
 */
export function MembersView({
  members,
  canManage = isOrgAdmin(),
}: {
  members: readonly MemberRow[];
  canManage?: boolean;
}): SafeHtml {
  const dialog = memberDialog.value;
  const editing = dialog?.kind === 'edit' ? members.find((m) => m.id === dialog.memberId) : undefined;
  const organizationName =
    orgs.value.find((org) => org.current)?.name ??
    (settings.value?.profile as { name?: string } | undefined)?.name ??
    'this organization';
  return (
    <List gap="m" controlInsets="tb" className="members-view members-page">
      {canManage ? (
        <List rootAttributes={{ 'data-members-list': '' }}>
          {/* Dividers only between rows, so Kerf rounds the first row's top and the last row's
              bottom; a lone row stays fully rounded (HS2-1CPAMR, KF-AQN4H7). */}
          {members.map((m, index) => {
            const name = memberDisplayName(m);
            return (
              <ListItem
                label={name}
                description={name === m.email ? undefined : m.email}
                status={`${m.roles.join(', ')} · ${m.status}`}
                action="member-open"
                itemId={m.id}
                accessibleLabel={`Edit ${name}`}
                divider={index < members.length - 1 ? 'after' : 'none'}
                multiline
              />
            );
          })}
        </List>
      ) : (
        <ValueTable label="Organization Members">
          {members.map((m) => {
            const name = memberDisplayName(m);
            const details = [...(name === m.email ? [] : [m.email]), m.roles.join(', '), m.status].join(' · ');
            return <ValueTableRow label={name} value={details} />;
          })}
        </ValueTable>
      )}
      {canManage && dialog?.kind === 'invite' ? (
        <MemberInviteDialog error={dialog.error} organizationName={organizationName} />
      ) : null}
      {canManage && editing !== undefined && dialog?.kind === 'edit' ? (
        <MemberDialog member={editing} error={dialog.error} />
      ) : null}
    </List>
  );
}
